<template>
	<div v-if="!hide">
		<div class="countdown-timer flex-row" :class="{ 'hide-mobile': hideMobile }">
			<div v-for="card in displayCards" :key="card.title" class="d-flex flex-row align-items-center justify-content-center">
				<div class="count-card">{{ card.value }}{{ card.title }}</div>
			</div>
		</div>
	</div>
</template>
<script setup>
	import { onMounted, ref, defineProps } from 'vue'
	import {
		differenceInMonths,
		differenceInDays,
		differenceInHours,
		differenceInMinutes,
		differenceInSeconds,
		addMonths,
		addDays,
		addHours,
		addMinutes,
	} from 'date-fns'

	const emit = defineEmits('timerEnded')
	const props = defineProps({
		endDate: {
			type: Date,
		},
		hideMobile: {
			type: Boolean,
			defalt: false,
		},
	})

	let lastUpdatedTime = 0
	const hide = ref(true)
	const months = ref(0)
	const days = ref(0)
	const hours = ref(0)
	const minutes = ref(0)
	const seconds = ref(0)
	const displayCards = ref([])

	onMounted(() => {
		updateTimer()
	})

	function updateTimer() {
		lastUpdatedTime = new Date().getTime()
		calculateDateDiffs()
		updateCardstoShow()
		// Once timer is up no need to keep calculating
		if (hide.value) {
			return
		}
		// Get as close as we can to one second for next update
		const currentTime = new Date().getTime()
		const timeElapsed = currentTime - lastUpdatedTime
		const nextTick = timeElapsed > 0 ? 1000 - timeElapsed : 0
		setTimeout(updateTimer, nextTick)
	}

	function calculateDateDiffs() {
		const currentDate = new Date()
		const endDate = new Date(props.endDate)

		if (isNaN(endDate.getTime())) {
			hide.value = true
			return
		}

		if (currentDate >= endDate) {
			hide.value = true
			emit('timerEnded')
			return
		}

		hide.value = false

		let adjustedDate = currentDate

		months.value = differenceInMonths(endDate, currentDate)
		adjustedDate = addMonths(currentDate, months.value)

		days.value = differenceInDays(endDate, adjustedDate)
		adjustedDate = addDays(adjustedDate, days.value)

		hours.value = differenceInHours(endDate, adjustedDate)
		adjustedDate = addHours(adjustedDate, hours.value)

		minutes.value = differenceInMinutes(endDate, adjustedDate)
		adjustedDate = addMinutes(adjustedDate, minutes.value)

		seconds.value = differenceInSeconds(endDate, adjustedDate)
	}

	function updateCardstoShow() {
		const out = []
		if (months.value > 0) {
			out.push({
				value: months.value,
				title: 'm',
			})
		}

		// If months shows days must be shown also
		if (days.value > 0 || months.value > 0) {
			out.push({
				value: days.value,
				title: 'd',
			})
		}

		// Always show hours if there are any
		if (hours.value > 0 || days.value > 0 || months.value > 0) {
			out.push({
				value: hours.value,
				title: 'h',
			})
		}

		// Always show minutes and seconds
		out.push({
			value: minutes.value,
			title: 'm',
		})

		out.push({
			value: seconds.value,
			title: 's',
		})

		displayCards.value = out
	}
</script>
<style lang="scss">
	.countdown-timer {
		gap: 4px;
		display: flex;
		width: 150px;
		margin: 5px;

		@media screen and (max-width: 900px) {
			&.hide-mobile {
				display: none;
			}
		}

		.count-card {
			color: #fff;
			font-family: 'Plus Jakarta Sans';
			font-size: 16px;
			font-style: normal;
			font-weight: 500;
			line-height: 24px; /* 150% */
			letter-spacing: 0.16px;
		}
	}
</style>
